import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AftersalesDelivery } from '@1po/1po-bff-fe-spec/generated/aftersales_order/model/AftersalesDelivery';
import {
  AftersalesOrder,
  AftersalesOrderItem,
} from '@1po/1po-bff-fe-spec/generated/aftersales_order/model/AftersalesOrder';
import { ClaimItem } from '@1po/1po-bff-fe-spec/generated/claim/request/CreateOrderClaimRequest';
import { Dropdown } from 'antd-v5';
import { ChevronDownIcon } from 'assets/icons';
import { initialState, setOrderClaimForm } from 'domains/order/Order.store';
import { ClaimFormGroupType, MULTI_ITEMS_DIRECT_DELIVERY, MULTI_ITEMS_FULL_OTC } from 'domains/order/Order.types';
import { getMultiItemsFormType } from 'domains/order/Order.utils';
import { theme } from 'styles';
import { BlackButton, Flex, Icon, Pipeline, Text } from 'UI';

interface CreateMultiItemsClaimButtonProps {
  orderDetail: AftersalesOrder;
  setIsOpen: (b: boolean) => void;
}

interface MultiClaimItem {
  deliveryNumber?: string;
  packageNumber?: string;
  invoiceNumber: string;
  claimItems: ClaimItem[];
}

const createClaimItem = (item: AftersalesOrderItem, delivery: AftersalesDelivery): ClaimItem => ({
  invoiceNumber: delivery.invoice?.invoiceNumber,
  invoiceLineNumber: delivery.invoice?.invoiceLineNumber,
  referenceNumber: item.reference,
  quantityInvoiced: Number(delivery.invoice?.invoiceQuantity),
});

const addClaimItem = (
  itemsByNumber: Map<string, MultiClaimItem>,
  itemKey: string | undefined,
  deliveryNumber: string | undefined,
  packageNumber: string | undefined,
  invoiceNumber: string | undefined,
  claimItem: ClaimItem,
) => {
  if (!itemKey || !claimItem.referenceNumber) {
    return;
  }
  const existing = itemsByNumber.get(itemKey);
  if (existing) {
    existing.claimItems.push(claimItem);
  } else {
    itemsByNumber.set(itemKey, {
      deliveryNumber,
      packageNumber,
      invoiceNumber: invoiceNumber ?? '',
      claimItems: [claimItem],
    });
  }
};

const addColisClaimItems = (
  itemsByNumber: Map<string, MultiClaimItem>,
  item: AftersalesOrderItem,
  delivery: AftersalesDelivery,
) => {
  (delivery.invoice?.colis ?? []).forEach((colis) => {
    addClaimItem(
      itemsByNumber,
      colis.colisNumber,
      delivery.deliveryNumber,
      colis.colisNumber,
      delivery.invoice?.invoiceNumber,
      createClaimItem(item, delivery),
    );
  });
};

const processDelivery = (
  itemsByNumber: Map<string, MultiClaimItem>,
  formType: ClaimFormGroupType | undefined,
  item: AftersalesOrderItem,
  delivery: AftersalesDelivery,
) => {
  if (formType === MULTI_ITEMS_DIRECT_DELIVERY) {
    addClaimItem(
      itemsByNumber,
      delivery.deliveryNumber,
      delivery.deliveryNumber,
      undefined,
      delivery.invoice?.invoiceNumber,
      createClaimItem(item, delivery),
    );
  }
  if (formType === MULTI_ITEMS_FULL_OTC) {
    addColisClaimItems(itemsByNumber, item, delivery);
  }
};

const sortClaimItems = (item: MultiClaimItem) => {
  item.claimItems.sort((a, b) => {
    const invoiceLineNumberDiff = (a.invoiceLineNumber ?? 0) - (b.invoiceLineNumber ?? 0);
    if (invoiceLineNumberDiff !== 0) {
      return invoiceLineNumberDiff;
    }
    return (a.referenceNumber ?? '').localeCompare(b.referenceNumber ?? '');
  });
};

const CreateMultiItemsClaimButton = ({ orderDetail, setIsOpen }: CreateMultiItemsClaimButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectOptionOpen, setSelectOptionOpen] = useState(false);

  const formType = getMultiItemsFormType(orderDetail);

  const getLabel = () => {
    switch (formType) {
      case MULTI_ITEMS_DIRECT_DELIVERY:
        return t('order.claim.action.create_delivery_claim', 'Create delivery claim');
      case MULTI_ITEMS_FULL_OTC:
        return t('order.claim.action.create_package_claim', 'Create package claim');
      default:
        return undefined;
    }
  };

  const getSelectLabel = () => {
    switch (formType) {
      case MULTI_ITEMS_DIRECT_DELIVERY:
        return t('order.claim.action.select_delivery', 'Select Delivery');
      case MULTI_ITEMS_FULL_OTC:
        return t('order.claim.action.select_package', 'Select Package');
      default:
        return undefined;
    }
  };

  const getItemNumber = (item: MultiClaimItem) => {
    switch (formType) {
      case MULTI_ITEMS_DIRECT_DELIVERY:
        return item.deliveryNumber ?? '';
      case MULTI_ITEMS_FULL_OTC:
        return item.packageNumber ?? '';
      default:
        return '';
    }
  };

  const getMultiClamItems = (): MultiClaimItem[] => {
    const orderItems = orderDetail.items ?? [];
    const itemsByNumber = new Map<string, MultiClaimItem>();

    orderItems.forEach((item) => {
      const deliveries = item.purchaseOrder?.purchaseOrderItem?.deliveries ?? [];
      deliveries.forEach((delivery) => processDelivery(itemsByNumber, formType, item, delivery));
    });

    itemsByNumber.forEach(sortClaimItems);

    return Array.from(itemsByNumber.values()).sort((a, b) => getItemNumber(a).localeCompare(getItemNumber(b)));
  };

  const label = getLabel();
  const selectLabel = getSelectLabel();
  const multiClaimItems = getMultiClamItems();

  if (!formType || !label || !selectLabel) {
    return null;
  }

  const handleClick = (item: MultiClaimItem) => {
    dispatch(
      setOrderClaimForm({
        ...initialState.claims.claimForm,
        formType,
        orderNumber: orderDetail.salesOrderNumber,
        reason: 'Y07',
        deliveryNumber: item.deliveryNumber,
        packageNumber: item.packageNumber,
        invoiceNumber: item.invoiceNumber,
        soldToPartyId: orderDetail.soldToPartyId,
        shipToPartyId: orderDetail.shipToPartyId,
        claimItems: item.claimItems,
      }),
    );
    setIsOpen(true);
  };

  const items = [
    {
      label: <Text type="base">{selectLabel}</Text>,
      key: 0,
      disabled: true,
    },
    ...multiClaimItems.map((item) => {
      const number = getItemNumber(item);
      return {
        label: number,
        key: number,
        onClick: () => handleClick(item),
      };
    }),
  ];

  return (
    <Dropdown menu={{ items }} open={selectOptionOpen} onOpenChange={setSelectOptionOpen} trigger={['click']}>
      <BlackButton>
        <Flex gap={15} align={'center'}>
          {label}
          <Pipeline size={40} color={theme.color.white} />
          <Icon IconComponent={ChevronDownIcon} size={16} />
        </Flex>
      </BlackButton>
    </Dropdown>
  );
};

export default CreateMultiItemsClaimButton;
